import { datadogRum } from "@datadog/browser-rum";
import type { RumEvent, RumEventDomainContext } from "@datadog/browser-rum";

const KNOWN_NOISY_ERROR_SUBSTRINGS = [
  "ResizeObserver loop completed with undelivered notifications",
];

const KNOWN_NOISY_ERROR_SUBSTRINGS_IN_LOWERCASE =
  KNOWN_NOISY_ERROR_SUBSTRINGS.map((substr) => substr.toLowerCase());

const isKnownNoisyError = (rawMessage: string): boolean =>
  KNOWN_NOISY_ERROR_SUBSTRINGS_IN_LOWERCASE.some((substr) =>
    rawMessage.includes(substr)
  );

const EMPTY_REJECTION_MESSAGES = new Set([
  "",
  "empty reason",
  "undefined",
  "null",
]);

const toMessage = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  if (value instanceof Error) {
    return value.message;
  }
  if (value === undefined) {
    return "undefined";
  }
  if (value === null) {
    return "null";
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const isEmptyRejectionMessage = (message: string): boolean =>
  EMPTY_REJECTION_MESSAGES.has(message.trim().toLowerCase());

export const applyUnhandledRejectionBeforeSendFilter = (
  event: RumEvent,
  context: RumEventDomainContext
): boolean => {
  if (event?.type !== "error") {
    return true;
  }

  const eventMessage = toMessage(
    (event as any)?.error?.message ??
      (event as any)?.message ??
      (context as any)?.error
  );
  const contextMessage = toMessage(
    (context as any)?.error?.message ??
      (context as any)?.error ??
      (context as any)?.reason
  );
  const rawMessage = `${eventMessage} ${contextMessage}`.toLowerCase();

  const isPromiseRejectionEvent =
    rawMessage.includes("unhandled") ||
    rawMessage.includes("rejection") ||
    rawMessage.includes("uncaught");

  if (isPromiseRejectionEvent) {
    const eventContext = (event.context || {}) as Record<string, unknown>;
    eventContext.rejectionSource = "window.unhandledrejection";
    eventContext.rejectionMessage = eventMessage || contextMessage || "";
  }

  const hasEmptyReason =
    isEmptyRejectionMessage(eventMessage) ||
    isEmptyRejectionMessage(contextMessage) ||
    rawMessage.includes("empty reason");

  const shouldDrop =
    (isPromiseRejectionEvent && hasEmptyReason) ||
    isKnownNoisyError(rawMessage);

  return !shouldDrop;
};

export const initializeDatadog = (
  applicationId: string,
  clientToken: string,
  sessionReplayRate: number,
  env: string,
  enableUnhandledRejectionGuard: boolean
) => {
  datadogRum.init({
    applicationId,
    clientToken,
    site: "datadoghq.com",
    service: "partscenter",
    env,
    version: process.env.APP_VERSION,
    sessionSampleRate: 100,
    sessionReplaySampleRate: sessionReplayRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: enableUnhandledRejectionGuard
      ? applyUnhandledRejectionBeforeSendFilter
      : undefined,
    allowedTracingUrls: [
      /^https:\/\/partscenter\.deere\.com(?:\/|$)/,
      /^https:\/\/parts-jdpo\.deere\.com(?:\/|$)/,
    ],
  });
};
